import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@design-system';
import { AlertTriangle, ArrowUpRight, Check, X } from 'lucide-react';
import {
  useWizardEvalRunResults,
  type WizardEvalRunResultRow,
} from '@/features/wizard-evals';
import {
  LoadingBlock,
  RefreshButton,
} from '@/features/wizard-evals/components/WizardEvalsLoading';

const ALL = 'all';

function fmtPct(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtF1(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function effectiveF1(r: WizardEvalRunResultRow['result']): number | null {
  if (r.hasOverrides && Number.isFinite(r.effectiveF1)) return r.effectiveF1;
  return r.ruleF1;
}

interface Aggregates {
  count: number;
  outcomeAccuracy: number;
  rateClassAccuracy: number;
  macroF1: number;
  tp: number;
  fp: number;
  fn: number;
  microPrecision: number;
  microRecall: number;
  microF1: number;
}

function aggregate(rows: WizardEvalRunResultRow[]): Aggregates {
  const n = rows.length;
  if (n === 0) {
    return {
      count: 0,
      outcomeAccuracy: 0,
      rateClassAccuracy: 0,
      macroF1: 0,
      tp: 0,
      fp: 0,
      fn: 0,
      microPrecision: 0,
      microRecall: 0,
      microF1: 0,
    };
  }
  let outcomeOk = 0;
  let rateClassConsidered = 0;
  let rateClassOk = 0;
  let f1Sum = 0;
  let f1Count = 0;
  let tp = 0;
  let fp = 0;
  let fn = 0;
  for (const row of rows) {
    const r = row.result;
    if (r.outcomeMatch) outcomeOk += 1;
    if (r.expectedRateClass != null) {
      rateClassConsidered += 1;
      if (r.rateClassMatch) rateClassOk += 1;
    }
    const f1 = effectiveF1(r);
    if (f1 != null) {
      f1Sum += f1;
      f1Count += 1;
    }
    tp += r.effectiveTruePositives?.length ?? r.truePositives.length;
    fp += r.effectiveFalsePositives?.length ?? r.falsePositives.length;
    fn += r.effectiveFalseNegatives?.length ?? r.falseNegatives.length;
  }
  const microPrecision = tp + fp === 0 ? 0 : tp / (tp + fp);
  const microRecall = tp + fn === 0 ? 0 : tp / (tp + fn);
  const microF1 =
    microPrecision + microRecall === 0
      ? 0
      : (2 * microPrecision * microRecall) / (microPrecision + microRecall);
  return {
    count: n,
    outcomeAccuracy: outcomeOk / n,
    rateClassAccuracy: rateClassConsidered === 0 ? 0 : rateClassOk / rateClassConsidered,
    macroF1: f1Count === 0 ? 0 : f1Sum / f1Count,
    tp,
    fp,
    fn,
    microPrecision,
    microRecall,
    microF1,
  };
}

function YesNo({ ok, label }: { ok: boolean; label?: string }) {
  const icon = ok ? (
    <Check className="h-3.5 w-3.5 text-emerald-600" />
  ) : (
    <X className="h-3.5 w-3.5 text-red-600" />
  );
  if (!label) return icon;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center">{icon}</span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">{label}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function RunResultsTable() {
  const { data, isLoading, isFetching, isError, error, refetch } = useWizardEvalRunResults();
  const rows = data?.rows ?? [];

  const [latestOnly, setLatestOnly] = useState(true);
  const [carrier, setCarrier] = useState<string>(ALL);
  const [modelVersion, setModelVersion] = useState<string>(ALL);
  const [promptVersion, setPromptVersion] = useState<string>(ALL);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const carriers = useMemo(
    () =>
      [
        ...new Set(rows.map(r => r.testCase?.carrier).filter((c): c is string => !!c)),
      ].sort(),
    [rows],
  );
  const modelVersions = useMemo(
    () =>
      [
        ...new Set(rows.map(r => r.run?.modelVersion).filter((v): v is string => !!v)),
      ].sort(),
    [rows],
  );
  const promptVersions = useMemo(
    () =>
      [
        ...new Set(rows.map(r => r.run?.promptVersion).filter((v): v is string => !!v)),
      ].sort(),
    [rows],
  );

  const visibleRows = useMemo(() => {
    let filtered = rows;
    if (carrier !== ALL) {
      filtered = filtered.filter(r => r.testCase?.carrier === carrier);
    }
    if (modelVersion !== ALL) {
      filtered = filtered.filter(r => r.run?.modelVersion === modelVersion);
    }
    if (promptVersion !== ALL) {
      filtered = filtered.filter(r => r.run?.promptVersion === promptVersion);
    }
    if (latestOnly) {
      const byTestCase = new Map<string, WizardEvalRunResultRow>();
      for (const row of filtered) {
        const tcId = row.testCase?.testCaseId ?? row.result.testCaseId;
        const ts = row.run?.createdDateTime ?? row.result.createdDateTime;
        const existing = byTestCase.get(tcId);
        if (
          !existing ||
          new Date(ts).getTime() >
            new Date(
              existing.run?.createdDateTime ?? existing.result.createdDateTime,
            ).getTime()
        ) {
          byTestCase.set(tcId, row);
        }
      }
      filtered = [...byTestCase.values()];
    }
    return filtered.sort((a, b) => {
      const aTs = new Date(
        a.run?.createdDateTime ?? a.result.createdDateTime,
      ).getTime();
      const bTs = new Date(
        b.run?.createdDateTime ?? b.result.createdDateTime,
      ).getTime();
      return bTs - aTs;
    });
  }, [rows, carrier, modelVersion, promptVersion, latestOnly]);

  const selectedRows = useMemo(
    () => visibleRows.filter(r => selectedIds.has(r.result.runResultId)),
    [visibleRows, selectedIds],
  );

  const aggregateRows = selectedRows.length > 0 ? selectedRows : visibleRows;
  const agg = useMemo(() => aggregate(aggregateRows), [aggregateRows]);

  const mixedModel =
    new Set(aggregateRows.map(r => r.run?.modelVersion).filter(Boolean)).size > 1;
  const mixedPrompt =
    new Set(aggregateRows.map(r => r.run?.promptVersion).filter(Boolean)).size > 1;

  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every(r => selectedIds.has(r.result.runResultId));

  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const r of visibleRows) next.delete(r.result.runResultId);
      } else {
        for (const r of visibleRows) next.add(r.result.runResultId);
      }
      return next;
    });
  };

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setCarrier(ALL);
    setModelVersion(ALL);
    setPromptVersion(ALL);
    setLatestOnly(true);
    setSelectedIds(new Set());
  };

  if (isError) {
    return (
      <p className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Failed to load run results: {(error as Error)?.message ?? 'unknown error'}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-800">
          <Switch
            checked={latestOnly}
            onCheckedChange={setLatestOnly}
          />
          Latest per test case
        </label>

        <Select
          value={carrier}
          onValueChange={setCarrier}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Carrier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All carriers</SelectItem>
            {carriers.map(c => (
              <SelectItem
                key={c}
                value={c}
              >
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={modelVersion}
          onValueChange={setModelVersion}
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Model" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All models</SelectItem>
            {modelVersions.map(v => (
              <SelectItem
                key={v}
                value={v}
              >
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={promptVersion}
          onValueChange={setPromptVersion}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Prompt version" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All prompt versions</SelectItem>
            {promptVersions.map(v => (
              <SelectItem
                key={v}
                value={v}
              >
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
        >
          Reset
        </Button>

        <p className="ml-auto text-sm text-gray-600">
          {isLoading
            ? ' '
            : `${visibleRows.length} row${visibleRows.length === 1 ? '' : 's'}${
                selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ''
              }`}
        </p>
        <RefreshButton
          onClick={() => refetch()}
          isFetching={isFetching}
        />
      </div>

      {isLoading && <LoadingBlock label="Loading run results…" />}

      <div className="rounded border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <Checkbox
                  checked={allVisibleSelected}
                  onCheckedChange={toggleAllVisible}
                  aria-label="Select all visible"
                />
              </TableHead>
              <TableHead>Test case</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>Run</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Prompt</TableHead>
              <TableHead className="text-center">Outcome</TableHead>
              <TableHead className="text-center">Rate class</TableHead>
              <TableHead className="text-right">F1</TableHead>
              <TableHead className="text-right">TP</TableHead>
              <TableHead className="text-right">FP</TableHead>
              <TableHead className="text-right">FN</TableHead>
              <TableHead>Triggered by</TableHead>
              <TableHead className="w-8" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {!isFetching && visibleRows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={14}
                  className="py-8 text-center text-sm text-gray-500"
                >
                  No runs match the current filters.
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map(row => {
              const r = row.result;
              const f1 = effectiveF1(r);
              const tp = r.effectiveTruePositives?.length ?? r.truePositives.length;
              const fp = r.effectiveFalsePositives?.length ?? r.falsePositives.length;
              const fn = r.effectiveFalseNegatives?.length ?? r.falseNegatives.length;
              return (
                <TableRow key={r.runResultId}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(r.runResultId)}
                      onCheckedChange={() => toggleRow(r.runResultId)}
                      aria-label="Select row"
                    />
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate font-medium text-gray-900">
                    {row.testCase?.name ?? r.testCaseId.slice(0, 8)}
                  </TableCell>
                  <TableCell className="text-gray-700">
                    {row.testCase?.carrier ?? '—'}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-gray-700">
                    {fmtDate(row.run?.createdDateTime ?? r.createdDateTime)}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-gray-700">
                    {row.run?.modelVersion ?? '—'}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-gray-700">
                    {row.run?.promptVersion ?? '—'}
                  </TableCell>
                  <TableCell className="text-center">
                    <YesNo
                      ok={r.outcomeMatch}
                      label={`expected ${r.expectedOutcome ?? '—'} · actual ${r.actualOutcome ?? '—'}`}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <YesNo
                      ok={r.rateClassMatch}
                      label={`expected ${r.expectedRateClass ?? 'n/a'} · actual ${r.actualRateClass ?? '—'}`}
                    />
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {r.hasOverrides ? (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help text-emerald-700">
                              {fmtF1(f1)} ✓
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <span className="text-xs">
                              raw {fmtF1(r.ruleF1)} → adjusted {fmtF1(r.effectiveF1)}
                            </span>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      fmtF1(f1)
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">{tp}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fp}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fn}</TableCell>
                  <TableCell className="max-w-[160px] truncate text-xs text-gray-700">
                    {row.run?.triggeredBy ?? '—'}
                  </TableCell>
                  <TableCell>
                    {row.run?.runId && (
                      <Link
                        to={`?tab=test-cases#${r.testCaseId}`}
                        title="Open run in test case panel"
                        className="text-primary hover:text-primary-hover"
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="rounded border border-primary-100 bg-primary-50 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-primary-dark">
            Aggregate · {selectedRows.length > 0 ? 'selected' : 'visible'} ({agg.count})
          </p>
          {(mixedModel || mixedPrompt) && (
            <span className="flex items-center gap-1 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" />
              Mixed {mixedModel && 'models'}
              {mixedModel && mixedPrompt && ' · '}
              {mixedPrompt && 'prompt versions'}
            </span>
          )}
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4 lg:grid-cols-6">
          <div>
            <dt className="text-xs text-gray-600">Outcome acc.</dt>
            <dd className="font-mono text-gray-900">{fmtPct(agg.outcomeAccuracy)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-600">Rate class acc.</dt>
            <dd className="font-mono text-gray-900">{fmtPct(agg.rateClassAccuracy)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-600">Macro F1</dt>
            <dd className="font-mono text-gray-900">{fmtF1(agg.macroF1)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-600">Micro F1</dt>
            <dd className="font-mono text-gray-900">{fmtF1(agg.microF1)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-600">Precision / Recall</dt>
            <dd className="font-mono text-gray-900">
              {fmtPct(agg.microPrecision)} / {fmtPct(agg.microRecall)}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-600">TP / FP / FN</dt>
            <dd className="font-mono text-gray-900">
              {agg.tp} / {agg.fp} / {agg.fn}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
